import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Drawer, Form, Input, InputNumber, Radio, Row, Select, Steps, Typography, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { putApi } from 'redux/sagas/putApiSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { API_STATUS, DATE_FORMAT, GYM_PACKAGES, PAYMENT_METHOD } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import dayjs from 'dayjs';
import { STATES_CITIES } from 'constants/states-cities';

const { Title } = Typography;
const { Step } = Steps;

const AddGym = ({ gymAddModal, width = '60%', editGym, setGymAddModal, refreshList, handleClose }) => {
	const [form] = Form.useForm();
	const stateValue = Form.useWatch('state', form);
	const dispatch = useDispatch();
	const globalRedux = useSelector((state) => state.globalRedux);
	const [current, setCurrent] = useState(0);

	const packageValue = Form.useWatch('package', form);
	const paymentMethodValue = Form.useWatch('paymentMethod', form);
	const amountValue = Form.useWatch('amount', form);
	const paidOnValue = Form.useWatch('paidOn', form);

	const stepFieldKeys = [
		['gymName', 'firstName', 'lastName', 'mobile', 'email', 'gstin', 'aadhaarNo', ...(editGym ? [] : ['password'])],
		...(!editGym ? [['branchName', 'branch_gstin', 'address', 'state', 'city', 'postalCode'],['package', 'amount', 'paidOn', 'nextBillDate', 'paymentMethod', 'upiTransactionId']] : []),
		['facebook', 'instagram', 'youtube'],
	];

	useEffect(() => {
		if (gymAddModal) {
			if (editGym) {
				setTimeout(() => {
					form.setFieldsValue({
						...editGym,
						facebook: editGym?.socialLinks?.facebook,
						instagram: editGym?.socialLinks?.instagram,
						youtube: editGym?.socialLinks?.youtube,
					});
				}, 0);
			} else {
				form.resetFields();
			}
		}
	}, [editGym, form, gymAddModal]);

	useEffect(() => {
		if (packageValue) {
			const gymPackage = GYM_PACKAGES.find((item) => item.value === packageValue);
			const paidOn = dayjs();
			form.setFieldsValue({
				amount: gymPackage?.price,
				total: gymPackage?.price,
				balance: 0,
				paidOn,
				nextBillDate: dayjs(paidOn).add(gymPackage?.days, 'days'),
			});
		}
	}, [form, packageValue]);

	useEffect(() => {
		if (amountValue) {
			const gymPackage = GYM_PACKAGES.find((item) => item.value === packageValue);
			const balance = gymPackage?.price - amountValue;
			form.setFieldsValue({ balance });
		}
	}, [amountValue, form, packageValue]);

	useEffect(() => {
		if (paidOnValue) {
			const gymPackage = GYM_PACKAGES.find((item) => item.value === packageValue);
			form.setFieldsValue({ nextBillDate: dayjs(paidOnValue).add(gymPackage?.days, 'days') });
		}
	}, [form, packageValue, paidOnValue]);

	useEffect(() => {
		if (editGym && globalRedux.apiStatus.EDIT_GYM === 'SUCCESS') {
    		console.log("EDIT_GYM SUCCESS detected, running refreshList");
			dispatch(resetApiStatus('EDIT_GYM'));
			form.resetFields();
			refreshList?.();
			setCurrent(0);
			handleClose?.();
		}

		if (!editGym && globalRedux.apiStatus.ADD_USER === 'SUCCESS') {
				dispatch(resetApiStatus('ADD_USER'));
				form.resetFields();
				refreshList?.();
				setCurrent(0);
				handleClose?.();
			}
	}, [
		globalRedux.apiStatus.EDIT_GYM,
		globalRedux.apiStatus.ADD_USER,
		editGym,
		dispatch,
		handleClose,
		refreshList,
		form,
	]);
			


	const handleSubmit = (values) => {
		const data = {
			firstName: values.firstName,
			lastName: values.lastName,
			gymDescription: values.gymDescription,
			gymName: values.gymName,
			gstin: values.gstin,
			email: values.email,
			...(!!!editGym && { password: values.password, role: 'admin' }),
			mobile: values.mobile,
			aadhaarNo: values.aadhaarNo,
			package: values.package,
			nextBillDate: values.nextBillDate,
			socialLinks: {
				facebook: values.facebook,
				instagram: values.instagram,
				youtube: values.youtube,
			},
			branch: {
				branchName: values?.branchName || '',
				gstin: values?.branch_gstin || '',
				isDefault: true,
				location: {
					address: values?.address || '',
					state: values?.state || '',
					city: values?.city || '',
					postalCode: values?.postalCode || '',
				},
			},
			paymentDetails: {
				package: values.package,
				amount: values.amount,
				balance: values.balance,
				paidOn: values.paidOn,
				nextBillDate: values.nextBillDate,
				paymentMethod: values.paymentMethod,
				...(paymentMethodValue === PAYMENT_METHOD[1]?.value && {
					upiTransactionId: values.upiTransactionId,
				}),
			},
		};
		console.log({data});
		if (editGym) {
			dispatch(putApi(data, 'EDIT_GYM', `${SERVER_IP}api/users/${editGym.id}`));
		} else {
			dispatch(postApi(data, 'ADD_USER'));
		}
	};

	const onNext = async () => {
		try {
			await form.validateFields(stepFieldKeys[current] || []);
			setCurrent(current + 1);
		} catch (_) {}
	};

	const onPrev = () => setCurrent(current - 1);

	const onCancel = () => {
		if (form.isFieldsTouched()) {
			Modal.confirm({
				title: 'Discard changes?',
				content: 'You have unsaved changes. Are you sure you want to discard them?',
				okText: 'Yes, Discard',
				cancelText: 'No, Stay',
				onOk: () => {
					setGymAddModal(false);
					form.resetFields();
					setCurrent(0);
				},
			});
		} else {
			setGymAddModal(false);
			form.resetFields();
			setCurrent(0);
		}
	};

	const cities = useMemo(() => {
		form.setFieldsValue({
			city: '',
		});
		return STATES_CITIES?.find((obj) => obj?.state === stateValue)?.districts || [];
	}, [form, stateValue]);

	const loading = globalRedux.apiStatus.ADD_USER === API_STATUS.PENDING || globalRedux.apiStatus.EDIT_GYM === API_STATUS.PENDING;

	const steps = [
		{
			title: 'Gym Info',
			content: (
				<Row gutter={[16, 16]}>
					<Col span={12}>
						<Form.Item label="Gym Display Name" name="gymName" rules={[{ required: true }]}>
							<Input placeholder="e.g. Gold's Gym Coimbatore" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Owner First Name" name="firstName" rules={[{ required: true }]}>
							<Input placeholder="Enter owner's first name" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Last Name" name="lastName">
							<Input placeholder="Enter owner's last name (optional)" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Mobile" name="mobile" rules={[{ required: true }]}>
							<Input maxLength={10} placeholder="10-digit mobile number" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Email" name="email" rules={[{ required: true }]}>
							<Input placeholder="you@example.com" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="GSTIN" name="gstin" rules={[{ required: true }]}>
							<Input placeholder="15-character GST number" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Aadhaar No" name="aadhaarNo" rules={[{ required: true }]}>
							<Input placeholder="12-digit Aadhaar number" />
						</Form.Item>
					</Col>
					{!editGym && (
						<Col span={12}>
							<Form.Item label="Password" name="password" rules={[{ required: true }]}>
								<Input.Password placeholder="Create admin login password" />
							</Form.Item>
						</Col>
					)}
					<Col span={24}>
						<Form.Item label="Description" name="gymDescription">
							<Input.TextArea rows={2} placeholder="Brief description about the gym" />
						</Form.Item>
					</Col>
				</Row>
			),
		},
		...(!editGym
			? [
					{
						title: 'Branch',
						content: (
							<Row gutter={[16, 16]}>
								<Col span={12}>
									<Form.Item
										label="Branch Name"
										name="branchName"
										rules={[
											{
												required: true,
												message: 'This Field is Required!',
											},
										]}>
										<Input placeholder="Enter Branch Name" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="GSTIN"
										name="branch_gstin"
										rules={[
											{
												required: true,
												message: 'This Field is Required!',
											},
										]}>
										<Input placeholder="Enter GSTIN" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Address"
										name="address"
										rules={[
											{
												required: true,
												message: 'This Field is Required!',
											},
										]}>
										<Input.TextArea placeholder="Enter Address" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="State"
										name="state"
										rules={[
											{
												required: true,
												message: 'This Field is Required!',
											},
										]}>
										<Select showSearch placeholder="select state">
											{STATES_CITIES?.map((obj) => (
												<Select.Option value={obj?.state}>{obj?.state}</Select.Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="City"
										name="city"
										rules={[
											{
												required: true,
												message: 'This Field is Required!',
											},
										]}>
										<Select showSearch placeholder="select city">
											{cities?.map((city) => city && <Select.Option value={city}>{city}</Select.Option>)}
										</Select>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="Postal Code"
										name="postalCode"
										rules={[
											{
												required: true,
												message: 'This Field is Required!',
											},
										]}>
										<InputNumber style={{ width: '100%' }} placeholder="Enter Postal Code" />
									</Form.Item>
								</Col>
							</Row>
						),
					},
					{
						title: 'Package & Payment',
						content: (
							<Row gutter={[16, 16]}>
								<Col span={12}>
									<Form.Item label="Package" name="package" rules={[{ required: true }]}>
										<Select placeholder="Choose subscription package">
											{GYM_PACKAGES.map((p) => (
												<Select.Option key={p.value} value={p.value}>
													{p.name}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								{packageValue && (
									<>
										<Col span={12}>
											<Form.Item label="Total" name="total">
												<InputNumber disabled style={{ width: '100%' }} placeholder="Total package amount" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Paid Amount" name="amount">
												<InputNumber style={{ width: '100%' }} placeholder="Amount paid by customer" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Balance" name="balance">
												<InputNumber disabled style={{ width: '100%' }} placeholder="Remaining balance" />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Paid On" name="paidOn" rules={[{ required: true }]}>
												<DatePicker
													format={DATE_FORMAT.DD_MM_YYYY}
													placeholder="Select payment date"
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Next Billing" name="nextBillDate" rules={[{ required: true }]}>
												<DatePicker
													format={DATE_FORMAT.DD_MM_YYYY}
													placeholder="Next billing date"
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
										<Col span={24}>
											<Form.Item label="Payment Method" name="paymentMethod" rules={[{ required: true }]}>
												<Radio.Group options={PAYMENT_METHOD} optionType="button" buttonStyle="solid" />
											</Form.Item>
										</Col>
										{paymentMethodValue === PAYMENT_METHOD[1]?.value && (
											<Col span={24}>
												<Form.Item label="UPI Transaction ID" name="upiTransactionId" rules={[{ required: true }]}>
													<Input placeholder="Enter UPI transaction reference ID" />
												</Form.Item>
											</Col>
										)}
									</>
								)}
							</Row>
						),
					},
			  ]
			: []),
		{
			title: 'Social Links',
			content: (
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Form.Item label="Facebook" name="facebook">
							<Input placeholder="e.g. https://facebook.com/yourgym" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="Instagram" name="instagram">
							<Input placeholder="e.g. https://instagram.com/yourgym" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="YouTube" name="youtube">
							<Input placeholder="e.g. https://youtube.com/yourgymchannel" />
						</Form.Item>
					</Col>
				</Row>
			),
		},
	];

	return (
		<Drawer open={gymAddModal} width={width} onClose={onCancel} closable={false} destroyOnClose bodyStyle={{ padding: 24 }}>
			<Card style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
				<Title level={4}>{editGym ? 'Edit Gym' : 'Add New Gym'}</Title>
				<Steps current={current} style={{ marginBottom: 32 }}>
					{steps.map((item) => (
						<Step key={item.title} title={item.title} />
					))}
				</Steps>
				<Form form={form} layout="vertical" onFinish={(values) => { console.log("On Finish clicked", { values }); handleSubmit(values); }}>
					{steps.map((step, index) => (
						<div key={step.title} style={{ display: index === current ? 'block' : 'none' }}>
							{step.content}
						</div>
					))}
					<Row justify="space-between" style={{ marginTop: 32 }}>
						{current > 0 && (
							<Button onClick={onPrev} style={{ width: '30%' }}>
								Previous
							</Button>
						)}
						{current < steps.length - 1 && (
							<Button type="primary" onClick={onNext} style={{ width: '30%' }}>
								Next
							</Button>
						)}
						{current === steps.length - 1 && (
							<Button type="primary" htmlType="submit" loading={loading} style={{ width: '30%' }}>
								{editGym ? 'Update' : 'Submit'}
							</Button>
						)}
					</Row>
				</Form>
			</Card>
		</Drawer>
	);
};

export default AddGym;
